// =============================================================================
// FILE: packages/renderer/src/components/public/SpatialEstimator.tsx
// PURPOSE: "Public Intelligence Portal — Spatial Estimator". A guest-facing
//   tool on the landing page. Emmy (R²) guides a visitor: enter Origin &
//   Destination (address OR lat,long → MOCK geocoder), we run the Haversine
//   formula, add 15% path slack, price against the Parts Library, apply the
//   labor + markup multipliers, and reveal a WATERMARKED estimate. Removing
//   the watermark / downloading requires starting the 100-day trial (lead).
// =============================================================================

import { useMemo, useState, type JSX } from 'react';
import { X, MapPin, Navigation, Sparkles, Lock, Rocket, Calculator, Check } from 'lucide-react';

const backendUrl = (import.meta.env.VITE_BACKEND_URL as string) ?? '';

// ─── Mock geocoder (no API key — deterministic, believable coordinates) ──────
interface Coord { lat: number; lng: number }

const KNOWN_PLACES: Record<string, Coord> = {
  calgary:   { lat: 51.0447, lng: -114.0719 },
  toronto:   { lat: 43.6532, lng: -79.3832 },
  vancouver: { lat: 49.2827, lng: -123.1207 },
  edmonton:  { lat: 53.5461, lng: -113.4938 },
  'new york':{ lat: 40.7128, lng: -74.0060 },
  chicago:   { lat: 41.8781, lng: -87.6298 },
  london:    { lat: 51.5074, lng: -0.1278 },
  seattle:   { lat: 47.6062, lng: -122.3321 },
};

function hashStr(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return (h >>> 0) / 4294967295;
}

/** Convert an address or "lat,lng" to coordinates. Mock — no external calls. */
function geocode(input: string): Coord | null {
  const t = input.trim().toLowerCase();
  if (!t) return null;
  const m = t.match(/^\s*(-?\d{1,3}(?:\.\d+)?)\s*,\s*(-?\d{1,3}(?:\.\d+)?)\s*$/);
  if (m) {
    const lat = parseFloat(m[1]!); const lng = parseFloat(m[2]!);
    if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat, lng };
  }
  for (const key of Object.keys(KNOWN_PLACES)) {
    if (t.includes(key)) {
      const base = KNOWN_PLACES[key]!;
      // small campus-scale jitter so two addresses in the same city still differ
      return { lat: base.lat + (hashStr(t) - 0.5) * 0.02, lng: base.lng + (hashStr(t + 'x') - 0.5) * 0.02 };
    }
  }
  // Deterministic fallback: base near Calgary + bounded offset (~ up to 1.5 km)
  const base = KNOWN_PLACES['calgary']!;
  return { lat: base.lat + (hashStr(t) - 0.5) * 0.03, lng: base.lng + (hashStr('_' + t) - 0.5) * 0.03 };
}

function haversineMeters(a: Coord, b: Coord): number {
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// ─── Parts Library (public subset) ───────────────────────────────────────────
const CABLES = {
  cat6:  { label: 'Cat6 UTP',  costPerFt: 0.62, vendor: 'CommScope' },
  cat6a: { label: 'Cat6A UTP', costPerFt: 1.10, vendor: 'CommScope' },
  fiber: { label: 'OS2 Fiber', costPerFt: 1.85, vendor: 'CommScope' },
} as const;
type CableKey = keyof typeof CABLES;

const LABOR_PRESETS = [
  { label: 'Standard',        mult: 1.00 },
  { label: 'Union',           mult: 1.35 },
  { label: 'Prevailing Wage', mult: 1.60 },
];
const LABOR_BASE_RATE = 0.40;
const PATH_SLACK = 0.15;

const usd = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export function SpatialEstimator({ onClose }: { onClose: () => void }): JSX.Element {
  const [origin, setOrigin]           = useState('Calgary Corporate HQ');
  const [destination, setDestination] = useState('Calgary Data Centre');
  const [cable, setCable]             = useState<CableKey>('cat6a');
  const [laborLabel, setLaborLabel]   = useState('Standard');
  const [laborMult, setLaborMult]     = useState(1.0);
  const [markupPct, setMarkupPct]     = useState(18);
  const [result, setResult]           = useState<null | ReturnType<typeof compute>>(null);
  const [geoError, setGeoError]       = useState<string | null>(null);

  const [trialEmail, setTrialEmail]   = useState('');
  const [trialDone, setTrialDone]     = useState(false);
  const [trialMsg, setTrialMsg]       = useState<string | null>(null);

  function compute() {
    const a = geocode(origin);
    const b = geocode(destination);
    if (!a || !b) return null;
    const meters = haversineMeters(a, b);
    const straightFt = meters * 3.28084;
    const runFt = straightFt * (1 + PATH_SLACK);
    const part = CABLES[cable];
    const materialCost = runFt * part.costPerFt;
    const laborCost = materialCost * LABOR_BASE_RATE * laborMult;
    const subtotal = materialCost + laborCost;
    const markupCost = subtotal * (markupPct / 100);
    const total = subtotal + markupCost;
    return { straightFt, runFt, materialCost, laborCost, markupCost, total, part };
  }

  const onCalculate = () => {
    setGeoError(null);
    const r = compute();
    if (!r) { setGeoError('Could not locate one of the points. Try a city name or "lat, long".'); return; }
    setResult(r);
  };

  const emmyMsg = useMemo(() => {
    if (!result) return '';
    return `I see a ${Math.round(result.runFt).toLocaleString()} ft run between these points. ` +
      `Based on current ${result.part.vendor} ${result.part.label} standards, your estimated cost is ` +
      `${usd(result.total)} USD. Would you like the full itemized breakdown?`;
  }, [result]);

  const startTrial = async () => {
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trialEmail)) { setTrialMsg('Please enter a valid work email.'); return; }
    setTrialDone(true);
    setTrialMsg('You\u2019re in! Check your inbox — your 100-day trial and itemized PDF are on the way.');
    try {
      await fetch(`${backendUrl}/api/marketplace/leads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kind: 'trial', email: trialEmail,
          item_name: `Spatial Estimate · ${origin} → ${destination}`,
          partner: 'commscope',
          price: result ? usd(result.total) : undefined,
          note: `cable=${cable}; labor=${laborLabel}; markup=${markupPct}%`,
        }),
      });
    } catch { /* lead capture is best-effort */ }
  };

  const teal = '#25B5A8';

  return (
    <div
      data-testid="spatial-estimator-modal"
      className="fixed inset-0 z-[10000] grid place-items-center p-4 overflow-y-auto"
      style={{ background: 'rgba(4,7,14,0.85)', backdropFilter: 'blur(8px)' }}
    >
      <div className="relative w-full max-w-3xl rounded-2xl my-6"
        style={{ background: 'linear-gradient(160deg, #0b1220, #0a0f1a)', border: `1px solid ${teal}44`, boxShadow: `0 30px 120px rgba(0,0,0,0.7)` }}>

        {/* header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${teal}22` }}>
          <div className="flex items-center gap-3">
            <div className="grid place-items-center rounded-lg" style={{ width: 40, height: 40, background: `${teal}1f`, border: `1px solid ${teal}55` }}>
              <Navigation className="h-5 w-5" style={{ color: teal }} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: `${teal}cc` }}>Public Intelligence Portal</div>
              <div className="text-base font-bold text-zinc-100">Spatial Estimator</div>
            </div>
          </div>
          <button type="button" data-testid="se-close" onClick={onClose}
            className="h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-zinc-200" style={{ border: '1px solid rgba(63,63,70,0.6)' }}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6">
          {/* Emmy intro */}
          <div className="flex items-start gap-3 mb-5 rounded-xl p-3" style={{ background: `${teal}0f`, border: `1px solid ${teal}2b` }}>
            <Sparkles className="h-4 w-4 mt-0.5 shrink-0" style={{ color: teal }} />
            <p className="text-[12.5px] text-zinc-300 leading-relaxed m-0" data-testid="se-emmy-intro">
              <span className="font-semibold" style={{ color: teal }}>R²:</span> Hi, I\u2019m Emmy. Give me two points — an address or
              <span className="font-mono text-[11px]"> lat, long</span> — and I\u2019ll estimate your cabling run in seconds.
            </p>
          </div>

          {/* Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5 flex items-center gap-1.5"><MapPin className="h-3 w-3" /> Origin</span>
              <input data-testid="se-origin-input" value={origin} onChange={(e) => setOrigin(e.target.value)}
                placeholder="Calgary, or 51.04,-114.07"
                className="w-full h-10 bg-zinc-900/60 border border-zinc-800 rounded-sm px-3 text-[13px] text-zinc-100 focus:outline-none focus:border-teal-400/60" />
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5 flex items-center gap-1.5"><Navigation className="h-3 w-3" /> Destination</span>
              <input data-testid="se-destination-input" value={destination} onChange={(e) => setDestination(e.target.value)}
                placeholder="Toronto, or 43.65,-79.38"
                className="w-full h-10 bg-zinc-900/60 border border-zinc-800 rounded-sm px-3 text-[13px] text-zinc-100 focus:outline-none focus:border-teal-400/60" />
            </label>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Cable type</span>
              <select data-testid="se-cable-select" value={cable} onChange={(e) => setCable(e.target.value as CableKey)}
                className="w-full h-10 bg-zinc-900/60 border border-zinc-800 rounded-sm px-2 text-[13px] text-zinc-100 focus:outline-none focus:border-teal-400/60">
                {Object.entries(CABLES).map(([k, v]) => <option key={k} value={k}>{v.label} · ${v.costPerFt}/ft</option>)}
              </select>
            </label>
            <div>
              <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Labor</span>
              <div className="grid grid-cols-3 gap-1.5">
                {LABOR_PRESETS.map((p) => (
                  <button key={p.label} type="button" data-testid={`se-labor-${p.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    onClick={() => { setLaborLabel(p.label); setLaborMult(p.mult); }}
                    className="h-10 rounded-sm text-[10px] font-semibold transition"
                    style={{ background: laborLabel === p.label ? `${teal}33` : 'rgba(30,41,59,0.5)', color: laborLabel === p.label ? '#5eead4' : '#94a3b8', border: `1px solid ${laborLabel === p.label ? teal + '88' : 'rgba(63,63,70,0.6)'}` }}>
                    {p.label.split(' ')[0]}<br />{p.mult.toFixed(2)}×
                  </button>
                ))}
              </div>
            </div>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Markup · <span className="text-teal-400" data-testid="se-markup-value">{markupPct}%</span></span>
              <input type="range" min={0} max={40} value={markupPct} data-testid="se-markup-slider"
                onChange={(e) => setMarkupPct(Number(e.target.value))} className="w-full accent-teal-500 mt-3" />
            </label>
          </div>

          {geoError && <div data-testid="se-geo-error" className="text-[12px] text-rose-300 mb-3">{geoError}</div>}

          <button type="button" data-testid="se-calculate-btn" onClick={onCalculate}
            className="w-full h-11 rounded-lg font-bold text-[13px] uppercase tracking-[0.12em] transition-transform hover:scale-[1.01] flex items-center justify-center gap-2"
            style={{ background: teal, color: '#04110f' }}>
            <Calculator className="h-4 w-4" /> Estimate Cabling Run
          </button>

          {/* Result */}
          {result && (
            <div className="mt-6" data-testid="se-result">
              {/* Emmy message */}
              <div className="flex items-start gap-3 mb-4 rounded-xl p-3" style={{ background: `${teal}0f`, border: `1px solid ${teal}2b` }}>
                <Sparkles className="h-4 w-4 mt-0.5 shrink-0" style={{ color: teal }} />
                <p className="text-[12.5px] text-zinc-200 leading-relaxed m-0" data-testid="se-emmy-msg">
                  <span className="font-semibold" style={{ color: teal }}>R²:</span> {emmyMsg}
                </p>
              </div>

              {/* Teaser total */}
              <div className="rounded-xl p-5 mb-4 text-center" style={{ background: `${teal}12`, border: `1px solid ${teal}44` }}>
                <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-400 mb-1">Rough Construction Estimate</div>
                <div className="text-4xl font-extrabold" style={{ color: teal }} data-testid="se-teaser-total">{usd(result.total)} <span className="text-lg text-zinc-500 font-semibold">USD</span></div>
                <div className="text-[11px] text-zinc-500 mt-1">
                  {Math.round(result.straightFt).toLocaleString()} ft straight-line · +15% path slack → <span className="text-zinc-300">{Math.round(result.runFt).toLocaleString()} ft billable</span>
                </div>
              </div>

              {/* Watermarked breakdown */}
              <div className="relative rounded-xl overflow-hidden" data-testid="se-breakdown" style={{ border: '1px solid rgba(148,172,210,0.15)' }}>
                <div className="p-5" style={{ filter: 'blur(0.4px)' }}>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 mb-3">Itemized Breakdown</div>
                  {[
                    [`${result.part.label} · ${Math.round(result.runFt).toLocaleString()} ft @ $${result.part.costPerFt}/ft`, usd(result.materialCost)],
                    [`Labor · ${laborLabel} (${laborMult.toFixed(2)}×)`, usd(result.laborCost)],
                    [`Markup · ${markupPct}%`, usd(result.markupCost)],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between py-1.5 text-[13px] border-b border-zinc-800/50">
                      <span className="text-zinc-400">{k}</span><span className="text-zinc-200 font-medium">{v}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-3 text-[14px] font-bold">
                    <span className="text-zinc-100">Total</span><span style={{ color: teal }}>{usd(result.total)}</span>
                  </div>
                </div>
                {/* watermark overlay */}
                <div aria-hidden data-testid="se-watermark" className="absolute inset-0 grid place-items-center pointer-events-none select-none"
                  style={{ background: 'rgba(6,10,18,0.35)' }}>
                  <span style={{ transform: 'rotate(-18deg)', fontSize: 34, fontWeight: 900, letterSpacing: 6, color: 'rgba(37,181,168,0.28)', border: '3px solid rgba(37,181,168,0.28)', padding: '6px 22px', borderRadius: 8 }}>
                    RCD² TRIAL · PREVIEW
                  </span>
                </div>
              </div>

              {/* Trial lead capture */}
              <div className="mt-5 rounded-xl p-5" style={{ background: 'rgba(252,211,77,0.06)', border: '1px solid rgba(252,211,77,0.28)' }}>
                {trialDone ? (
                  <div className="flex items-center gap-2 text-[13px]" style={{ color: '#6ee7b7' }} data-testid="se-trial-confirm">
                    <Check className="h-4 w-4" /> {trialMsg}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <Lock className="h-4 w-4 text-amber-300" />
                      <span className="text-[13px] font-semibold text-amber-200">Unlock the itemized PDF — start your 100-day trial</span>
                    </div>
                    <p className="text-[11.5px] text-zinc-400 m-0 mb-3">Remove the watermark and download the full estimate with parts, labor and terms.</p>
                    <div className="flex flex-col sm:flex-row gap-2">
                      <input data-testid="se-trial-email" type="email" value={trialEmail} onChange={(e) => setTrialEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="flex-1 h-10 bg-zinc-900/60 border border-zinc-800 rounded-sm px-3 text-[13px] text-zinc-100 focus:outline-none focus:border-amber-300/60" />
                      <button type="button" data-testid="se-start-trial-btn" onClick={startTrial}
                        className="h-10 px-5 rounded-sm font-bold text-[12px] uppercase tracking-[0.1em] bg-amber-300 text-zinc-900 hover:bg-amber-200 transition flex items-center justify-center gap-2 whitespace-nowrap">
                        <Rocket className="h-4 w-4" /> Start 100-Day Trial
                      </button>
                    </div>
                    {trialMsg && !trialDone && <p className="text-[11px] text-rose-300 mt-2 m-0" data-testid="se-trial-error">{trialMsg}</p>}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
